import React, { useState, useEffect } from 'react';
import { actionsApiService } from '../../api/actionsApi';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import DataFilter from '../../components/ui/DataFilter';
import DataTable from '../../components/ui/DataTable';
import DatePicker from '../../components/ui/DatePicker';
import { createColumnHelper } from '@tanstack/react-table';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

// DatePicker Component


const StopSaleActions = () => {
  const [hotels, setHotels] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [operators, setOperators] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [filters, setFilters] = useState({
    pazar: [], // Market - çoklu seçim
    acente: [], // Agency - çoklu seçim
    ilave: '', // İndirim yüzdesi (1-100)
    baslangic: '', // Start
    bitis: '', // End
    otel: [], // Hotel - çoklu seçim
    konaklamaBaslangic: '', // Accommodation Start Date
    konaklamaBitis: '', // Accommodation End Date
    aciklama: '' // Description
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    pazar: '',
    acente: '',
    ilave: '1',
    baslangic: '', // İndirim başlangıç tarihi
    bitis: '', // İndirim bitiş tarihi
    konaklamaBaslangic: '', // Konaklama başlangıç tarihi
    konaklamaBitis: '', // Konaklama bitiş tarihi
    otel: [],
    aciklama: ''
  });
  const [actionsData, setActionsData] = useState([]);
  const [isModalHotelDropdownOpen, setIsModalHotelDropdownOpen] = useState(false);
  const [filteredActionsData, setFilteredActionsData] = useState([]);
  const [editingId, setEditingId] = useState(null);

  // Data Loading
  const loadInitialData = async () => {
    try {
      const [hotelsRes, marketsRes, operatorsRes, agenciesRes] = await Promise.all([
        actionsApiService.getHotels(),
        actionsApiService.getMarkets(),
        actionsApiService.getOperators(),
        actionsApiService.getAgencies()
      ]);

      setHotels(Array.isArray(hotelsRes) ? hotelsRes : (hotelsRes.data || []));
      setMarkets(Array.isArray(marketsRes) ? marketsRes : (marketsRes.data || []));
      setOperators(Array.isArray(operatorsRes) ? operatorsRes : (operatorsRes.data || []));
      setAgencies(Array.isArray(agenciesRes) ? agenciesRes : (agenciesRes.data || []));
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadActions = async () => {
    try {
      const response = await actionsApiService.getActions();
      setActionsData(Array.isArray(response) ? response : (response.data || []));
    } catch (error) {
      console.error('Error loading actions:', error);
    }
  };

  useEffect(() => {
    loadInitialData();
    loadActions();
  }, []);

  // Filter Handlers
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      pazar: [],
      acente: [],
      ilave: '',
      baslangic: '',
      bitis: '',
      otel: [],
      konaklamaBaslangic: '',
      konaklamaBitis: '',
      aciklama: ''
    });
    setFilteredActionsData(actionsData);
  };

  const handleSearch = () => {
    let result = [...actionsData];

    if (filters.pazar && filters.pazar.length > 0) {
      result = result.filter(item => item.market === filters.pazar || (Array.isArray(filters.pazar) && filters.pazar.includes(item.market)));
    }

    if (filters.acente && filters.acente.length > 0) {
      result = result.filter(item => item.agency === filters.acente || (Array.isArray(filters.acente) && filters.acente.includes(item.agency)));
    }

    if (filters.otel && filters.otel.length > 0) {
      // Assuming loose match or add support for ID vs Name based on API data
      // For now, simple check
      result = result.filter(item => item.hotel == filters.otel || item.hotel_id == filters.otel || (Array.isArray(filters.otel) && filters.otel.includes(item.hotel)));
    }

    if (filters.ilave) {
      result = result.filter(item => item.extra == filters.ilave);
    }

    if (filters.baslangic) {
      result = result.filter(item => new Date(item.start_date) >= new Date(filters.baslangic));
    }

    if (filters.bitis) {
      result = result.filter(item => new Date(item.finish_date) <= new Date(filters.bitis));
    }

    if (filters.konaklamaBaslangic) {
      result = result.filter(item => new Date(item.accom_start) >= new Date(filters.konaklamaBaslangic));
    }

    if (filters.konaklamaBitis) {
      result = result.filter(item => new Date(item.accom_finish) <= new Date(filters.konaklamaBitis));
    }

    if (filters.aciklama) {
      result = result.filter(item => item.explanation && item.explanation.toLowerCase().includes(filters.aciklama.toLowerCase()));
    }

    setFilteredActionsData(result);
  };

  // Modal Handlers
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setEditingId(null);
    setModalData({
      pazar: '',
      acente: '',
      ilave: '1',
      baslangic: '',
      bitis: '',
      konaklamaBaslangic: '',
      konaklamaBitis: '',
      otel: [],
      aciklama: '',
      status: 'Active'
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleEditAction = (row) => {
    // row is the full data object from react-table row.original or similar
    const data = row.original || row;
    setEditingId(data.id);

    // Robust Agency Resolution
    let resolvedAgencyId = '';
    // 1. Try to find by direct ID match (if data.agency is an ID)
    let foundAgency = agencies.find(a => a.id == data.agency);
    // 2. If not found, try to find by Name (if data.agency is a Name)
    if (!foundAgency) {
      foundAgency = agencies.find(a => a.agency_name === data.agency);
    }
    // 3. If still not found, check agency_id field if it exists
    if (!foundAgency && data.agency_id) {
      foundAgency = agencies.find(a => a.id == data.agency_id);
    }

    if (foundAgency) {
      resolvedAgencyId = foundAgency.id;
    } else {
      // Fallback: If data.agency looks like an ID (numeric), use it directly even if not in list (to avoid losing it)
      if (!isNaN(data.agency) && data.agency !== null && data.agency !== '') {
        resolvedAgencyId = data.agency;
      }
    }

    // Robust Market Resolution
    let resolvedMarketId = '';
    let foundMarket = markets.find(m => m.id == data.market); // Try ID
    if (!foundMarket) {
      foundMarket = markets.find(m => m.market === data.market); // Try Name
    }
    if (!foundMarket && data.market_id) {
      foundMarket = markets.find(m => m.id == data.market_id);
    }

    if (foundMarket) {
      resolvedMarketId = foundMarket.id;
    } else if (!isNaN(data.market) && data.market !== null && data.market !== '') {
      resolvedMarketId = data.market;
    }

    setModalData({
      pazar: resolvedMarketId,
      acente: resolvedAgencyId,
      ilave: data.extra,
      baslangic: data.start_date,
      bitis: data.finish_date,
      konaklamaBaslangic: data.accom_start,
      konaklamaBitis: data.accom_finish,
      otel: data.hotel ? [data.hotel] : [],
      aciklama: data.explanation,
      status: data.status || 'Active'
    });
    setIsModalOpen(true);
  };

  const handleModalChange = (name, value) => {
    setModalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleHotelToggle = (hotelName, e) => {
    e.stopPropagation();
    setModalData(prev => {
      const currentHotels = prev.otel || [];
      const exists = currentHotels.includes(hotelName);
      let newHotels;
      if (exists) {
        newHotels = currentHotels.filter(h => h !== hotelName);
      } else {
        newHotels = [...currentHotels, hotelName];
      }
      return { ...prev, otel: newHotels };
    });
  };

  const handleModalSubmit = async () => {
    try {
      // Basic validation
      if (!modalData.pazar || !modalData.baslangic || !modalData.bitis) {
        alert('Lütfen gerekli alanları doldurunuz (Pazar, Başlangıç, Bitiş).');
        return;
      }

      // Find selected market to get both ID and Name
      const selectedMarket = markets.find(m => m.id == modalData.pazar);
      const marketName = selectedMarket ? selectedMarket.market : '';
      const marketId = selectedMarket ? selectedMarket.id : modalData.pazar; // Fallback to value if not found

      const payload = {
        market: marketName, // Legacy or main field, sending Name
        market_name: marketName, // Newly added DB field
        market_id: marketId,     // Newly added DB field
        agency: modalData.acente, // This is sending the ID currently. Consider if it needs same treatment?
        // Backend error was about 'agency' validation, likely accepting ID is fine if it acts as FK or raw ID.
        // BUT, to be safe, if you added market_name/id, maybe agency needs name too strictly?
        // User request only mentioned Market changes. I will stick to Market changes.
        extra: modalData.ilave,
        start_date: modalData.baslangic,
        finish_date: modalData.bitis,
        accom_start: modalData.konaklamaBaslangic,
        accom_finish: modalData.konaklamaBitis,
        hotel: modalData.otel.length > 0 ? modalData.otel[0] : null,
        explanation: modalData.aciklama,
        status: modalData.status || 'Active'
      };

      if (editingId) {
        await actionsApiService.updateAction(editingId, payload);
      } else {
        await actionsApiService.createAction(payload);
      }

      handleCloseModal();
      loadActions(); // Refresh data
    } catch (error) {
      console.error('Error saving action:', error);
      alert('Kaydetme hatası oluştu.');
    }
  };

  // Re-run search when actionsData changes to apply initial empty filters or keep current
  useEffect(() => {
    setFilteredActionsData(actionsData);
  }, [actionsData]);

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor('market', {
      header: 'PAZAR',
      cell: info => {
        const row = info.row.original;
        // 1. Try market_name (new DB field)
        if (row.market_name) return row.market_name;
        // 2. Try market (legacy/existing field if it has name)
        if (row.market && isNaN(row.market)) return row.market;
        // 3. Try resolving ID from state (if market or market_id looks like ID)
        const idToFind = row.market_id || row.market;
        const found = markets.find(m => m.id == idToFind);
        return found ? found.market : (row.market || '-');
      }
    }),
    columnHelper.accessor('agency', {
      header: 'ACENTE',
      cell: info => {
        const val = info.getValue();
        if (!val) return '-';
        // Try to find name if val looks like an ID
        const found = agencies.find(a => a.id == val);
        return found ? found.agency_name : val;
      }
    }),
    columnHelper.accessor('extra', {
      header: 'İLAVE (%)',
      cell: info => info.getValue() ? '%' + info.getValue() : '-'
    }),
    columnHelper.accessor('start_date', {
      header: 'BAŞLANGIÇ',
      cell: info => info.getValue() ? new Date(info.getValue()).toLocaleDateString('tr-TR') : '-'
    }),
    columnHelper.accessor('finish_date', {
      header: 'BİTİŞ',
      cell: info => info.getValue() ? new Date(info.getValue()).toLocaleDateString('tr-TR') : '-'
    }),
    columnHelper.accessor('hotel', {
      header: 'OTEL',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor(row => {
      if (row.accom_start && row.accom_finish) {
        return `${new Date(row.accom_start).toLocaleDateString('tr-TR')} - ${new Date(row.accom_finish).toLocaleDateString('tr-TR')}`;
      }
      return '-';
    }, {
      id: 'accom_range',
      header: 'KONAKLAMA ARALIĞI',
      cell: info => info.getValue()
    }),
    columnHelper.accessor('explanation', {
      header: 'AÇIKLAMA',
      cell: info => info.getValue() || '-'
    }),
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between pt-4">
        <div className="px-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Actions
          </h1>
        </div>
      </div>

      {/* Filters Section */}
      <div className="px-6">
        <div className="flex items-center justify-start">
          <Button
            onClick={handleOpenModal}
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
            title="Yeni Kayıt"
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="px-6">
        <DataFilter
          config={[
            { name: 'pazar', label: 'PAZAR', type: 'select', options: markets, allLabel: 'Seçiniz' },
            { name: 'acente', label: 'ACENTE', type: 'select', options: operators, allLabel: 'Seçiniz' },
            { name: 'otel', label: 'OTEL', type: 'select', options: hotels, allLabel: 'Seçiniz' },
            { name: 'baslangic', label: 'BAŞLANGIÇ', type: 'date' },
            { name: 'bitis', label: 'BİTİŞ', type: 'date' },
            // Advanced Filters
            { name: 'ilave', label: 'İLAVE (%)', type: 'text', placeholder: '%', advanced: true },
            { name: 'konaklamaBaslangic', label: 'KONAKLAMA BAŞLANGIÇ', type: 'date', advanced: true },
            { name: 'konaklamaBitis', label: 'KONAKLAMA BİTİŞ', type: 'date', advanced: true },
            { name: 'aciklama', label: 'AÇIKLAMA', type: 'text', placeholder: 'Ara...', advanced: true },
          ]}
          filters={filters}
          onChange={handleFilterChange}
          onClear={clearFilters}
          onSearch={handleSearch}
        />
      </div>










      {/* Results Table */}
      < div className="px-6 mt-6" >
        <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4">
            <div className="py-2">
              <DataTable
                data={filteredActionsData.length > 0 ? filteredActionsData : actionsData}
                columns={columns}
                isLoading={false} // Loading state not strictly managed here but could be added
                onRowClick={handleEditAction}
              />
            </div>
          </div>
        </Card>
      </div >

      {/* Modal */}
      {
        isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h2 className="text-xl font-medium text-white">
                  {editingId ? 'İndirimi Düzenle' : 'Yeni İndirim Ekle'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* Modal Form */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* PAZAR */}
                    <div className="space-y-2">
                      <label htmlFor="modal-pazar" className="block text-sm font-medium text-white mb-1">
                        Pazar
                      </label>
                      <select
                        id="modal-pazar"
                        value={modalData.pazar}
                        onChange={(e) => handleModalChange('pazar', e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Pazar seçiniz</option>
                        {markets.map((market) => (
                          <option key={market.id} value={market.id}>
                            {market.market}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ACENTE */}
                    <div className="space-y-2">
                      <label htmlFor="modal-acente" className="block text-sm font-medium text-white mb-1">
                        Acente
                      </label>
                      <select
                        id="modal-acente"
                        value={modalData.acente}
                        onChange={(e) => handleModalChange('acente', e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Acente seçiniz</option>
                        {agencies.map((agency) => (
                          <option key={agency.id} value={agency.id}>
                            {agency.agency_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* İLAVE (%) */}
                    <div className="space-y-2">
                      <label htmlFor="modal-ilave" className="block text-sm font-medium text-white mb-1">
                        İndirim Oranı (%)
                      </label>
                      <input
                        type="number"
                        id="modal-ilave"
                        value={modalData.ilave}
                        onChange={(e) => handleModalChange('ilave', e.target.value)}
                        min="1"
                        max="100"
                        placeholder="1-100 arası değer girin"
                        className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    {/* OTEL */}
                    <div className="space-y-2">
                      <label htmlFor="modal-otel" className="block text-sm font-medium text-white mb-1">
                        Otel Seçimi
                      </label>
                      <div className="relative modal-hotel-dropdown">
                        <div
                          className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white cursor-pointer flex items-center justify-between focus:border-blue-500"
                          onClick={() => setIsModalHotelDropdownOpen(!isModalHotelDropdownOpen)}
                        >
                          <span>
                            {modalData.otel.length === 0
                              ? 'Otel seçiniz'
                              : modalData.otel.length === 1
                                ? modalData.otel[0]
                                : `${modalData.otel.length} otel seçili`
                            }
                          </span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>

                        {isModalHotelDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded shadow-lg max-h-60 overflow-y-auto">
                            {hotels.map((hotel) => (
                              <div
                                key={hotel.id}
                                className="px-2 py-1.5 hover:bg-gray-600 cursor-pointer flex items-center justify-between"
                                onClick={(e) => handleHotelToggle(hotel.hotel_name, e)}
                              >
                                <span className="text-white text-xs truncate pr-2 whitespace-nowrap overflow-hidden">{hotel.hotel_name}</span>
                                {modalData.otel.includes(hotel.hotel_name) && (
                                  <svg className="w-3 h-3 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* İNDİRİM BAŞLANGIÇ */}
                    <div className="space-y-2">
                      <label htmlFor="modal-baslangic" className="block text-sm font-medium text-white mb-1">
                        İndirim Başlangıç Tarihi
                      </label>
                      <div className="relative">
                        <DatePicker
                          value={modalData.baslangic}
                          onChange={(value) => handleModalChange('baslangic', value.target ? value.target.value : value)}
                          placeholder="Tarih seçiniz"
                          name="modal-baslangic"
                        />
                      </div>
                    </div>

                    {/* İNDİRİM BİTİŞ */}
                    <div className="space-y-2">
                      <label htmlFor="modal-bitis" className="block text-sm font-medium text-white mb-1">
                        İndirim Bitiş Tarihi
                      </label>
                      <div className="relative">
                        <DatePicker
                          value={modalData.bitis}
                          onChange={(value) => handleModalChange('bitis', value.target ? value.target.value : value)}
                          placeholder="Tarih seçiniz"
                          name="modal-bitis"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* KONAKLAMA BAŞLANGIÇ */}
                    <div className="space-y-2">
                      <label htmlFor="modal-konaklama-baslangic" className="block text-sm font-medium text-white mb-1">
                        Konaklama Başlangıç Tarihi
                      </label>
                      <div className="relative">
                        <DatePicker
                          value={modalData.konaklamaBaslangic}
                          onChange={(value) => handleModalChange('konaklamaBaslangic', value.target ? value.target.value : value)}
                          placeholder="Tarih seçiniz"
                          name="modal-konaklama-baslangic"
                        />
                      </div>
                    </div>

                    {/* KONAKLAMA BİTİŞ */}
                    <div className="space-y-2">
                      <label htmlFor="modal-konaklama-bitis" className="block text-sm font-medium text-white mb-1">
                        Konaklama Bitiş Tarihi
                      </label>
                      <div className="relative">
                        <DatePicker
                          value={modalData.konaklamaBitis}
                          onChange={(value) => handleModalChange('konaklamaBitis', value.target ? value.target.value : value)}
                          placeholder="Tarih seçiniz"
                          name="modal-konaklama-bitis"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="modal-aciklama" className="block text-sm font-medium text-white mb-1">
                      Açıklama
                    </label>
                    <textarea
                      id="modal-aciklama"
                      value={modalData.aciklama}
                      onChange={(e) => handleModalChange('aciklama', e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:border-blue-500 focus:outline-none resize-none"
                      placeholder="İndirim hakkında açıklama veya notlar giriniz..."
                      rows="3"
                    />
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-700">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm text-gray-300 bg-gray-600 hover:bg-gray-500 rounded transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Temizle</span>
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleCloseModal}
                      className="px-4 py-2 text-sm text-gray-300 bg-gray-600 border border-gray-500 hover:bg-gray-500 rounded transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>İptal</span>
                    </button>
                    <button
                      onClick={handleModalSubmit}
                      className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>{editingId ? 'Güncelle' : 'İndirim Ekle'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default StopSaleActions;
